import React, { useMemo, useState } from "react";
import {
    Search,
    ChevronDown,
    Mail,
    MapPin,
    Box,
    FolderKanban,
    CheckCircle2,
    Clock,
    PauseCircle,
} from "lucide-react";

const PROJECTS = [
    {
        name: "EV Battery Enclosure",
        org: "Andhra Pradesh Innovation Corp",
        email: "evbattery.project@apic.gov.in",
        location: "Vizag",
        regNo: "APIC/PRJ/2023/001",
        status: "Active",
    },
    {
        name: "Agri Drone Chassis",
        org: "Andhra Pradesh Innovation Corp",
        email: "agridrone.project@apic.gov.in",
        location: "Vijayawada",
        regNo: "APIC/PRJ/2023/002",
        status: "Active",
    },
    {
        name: "IoT Smart Water Sensor",
        org: "Andhra Pradesh Innovation Corp",
        email: "smartwater.project@apic.gov.in",
        location: "Tirupati",
        regNo: "APIC/PRJ/2024/003",
        status: "Onboarding",
    },
    {
        name: "Medical Infusion Housing",
        org: "Andhra Pradesh Innovation Corp",
        email: "infusionhousing.project@apic.gov.in",
        location: "Kurnool",
        regNo: "APIC/PRJ/2024/004",
        status: "Inactive",
    },
];

const STATUS_STYLES = {
    Active: "bg-green-100 text-green-700",
    Onboarding: "bg-orange-100 text-orange-700",
    Inactive: "bg-gray-100 text-gray-600",
};

function StatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function Projects() {
    const [query, setQuery] = useState("");
    const [locationFilter, setLocationFilter] = useState("All Locations");
    const [statusFilter, setStatusFilter] = useState("All Status");

    const locations = useMemo(
        () => ["All Locations", ...new Set(PROJECTS.map((p) => p.location))],
        []
    );
    const statuses = useMemo(
        () => ["All Status", ...new Set(PROJECTS.map((p) => p.status))],
        []
    );

    const counts = useMemo(() => {
        const active = PROJECTS.filter((p) => p.status === "Active").length;
        const onboarding = PROJECTS.filter((p) => p.status === "Onboarding").length;
        const inactive = PROJECTS.filter((p) => p.status === "Inactive").length;
        return { total: PROJECTS.length, active, onboarding, inactive };
    }, []);

    const filtered = useMemo(() => {
        return PROJECTS.filter((p) => {
            const matchesQuery =
                query.trim() === "" ||
                [p.name, p.org, p.email].some((field) =>
                    field.toLowerCase().includes(query.trim().toLowerCase())
                );
            const matchesLocation =
                locationFilter === "All Locations" || p.location === locationFilter;
            const matchesStatus =
                statusFilter === "All Status" || p.status === statusFilter;
            return matchesQuery && matchesLocation && matchesStatus;
        });
    }, [query, locationFilter, statusFilter]);

    return (
        <div className="h-screen w-full overflow-y-auto bg-gray-50 scrollbar-hide">
            <style>{`
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}</style>

            <div className="p-6">
                {/* Page heading */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Track every prototyping project registered across Andhra Pradesh
                    </p>
                </div>

                {/* Stat cards */}
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <StatCard icon={FolderKanban} label="Total projects" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                    <StatCard icon={CheckCircle2} label="Active" value={counts.active} tone="bg-green-50 text-green-500" />
                    <StatCard icon={Clock} label="Onboarding" value={counts.onboarding} tone="bg-orange-50 text-orange-500" />
                    <StatCard icon={PauseCircle} label="Inactive" value={counts.inactive} tone="bg-gray-100 text-gray-500" />
                </div>

                {/* Table card */}
                <div className="w-full rounded-2xl border border-gray-200 bg-white shadow-sm">
                    {/* Header */}
                    <div className="flex flex-col gap-4 border-b border-gray-100 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Registered projects</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {filtered.length} of {PROJECTS.length} projects
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search name, org, email..."
                                    className="w-64 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={locationFilter}
                                    onChange={(e) => setLocationFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {locations.map((loc) => (
                                        <option key={loc} value={loc}>
                                            {loc}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="relative">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {statuses.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] table-fixed border-collapse text-left">
                            <thead>
                                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-400">
                                    <th className="w-1/4 px-6 py-3 font-medium">Project Name</th>
                                    <th className="w-1/5 px-6 py-3 font-medium">Organization</th>
                                    <th className="w-1/5 px-6 py-3 font-medium">Email</th>
                                    <th className="w-[12%] px-6 py-3 font-medium">Location</th>
                                    <th className="w-[16%] px-6 py-3 font-medium">Registration No.</th>
                                    <th className="w-[10%] px-6 py-3 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p, idx) => (
                                    <tr
                                        key={p.regNo}
                                        className={`text-sm ${idx !== filtered.length - 1 ? "border-b border-gray-100" : ""
                                            }`}
                                    >
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500">
                                                    <Box className="h-4 w-4" />
                                                </span>
                                                <span className="font-semibold text-gray-900">{p.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{p.org}</td>
                                        <td className="px-6 py-4 text-gray-500">
                                            <div className="flex items-center gap-2">
                                                <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                                                <span className="truncate">{p.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            <div className="flex items-center gap-1.5">
                                                <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                                                {p.location}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-500">{p.regNo}</td>
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${STATUS_STYLES[p.status] || "bg-gray-100 text-gray-600"
                                                    }`}
                                            >
                                                {p.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}

                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-400">
                                            No projects match your filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}